import { DetailRow } from "@/components/shared/finance/DetailRow";
import { MonoAmount } from "@/components/shared/finance/MonoAmount";
import { createPeekContent, PeekHeader } from "@/components/shared/relationships/peek-content";
import { registerPeekType } from "@/components/shared/relationships/peek-registry";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useExpenseDetail } from "@/features/expenses/hooks/use-expense-detail";
import { ABSENT_VALUE, formatCurrency, formatDate, formatEncodedDay } from "@/lib/format";

export function ExpenseContent({
  expense,
}: {
  expense: NonNullable<ReturnType<typeof useExpenseDetail>["data"]>;
}) {
  const categoryName = expense.gl_expense_categories?.category ?? expense.category;
  return (
    <>
      <PeekHeader
        badge={categoryName ? <Badge variant="secondary">{categoryName}</Badge> : null}
        title={expense.description || "Expense"}
      />
      <p className="font-semibold text-2xl">
        <MonoAmount>{formatCurrency(expense.amount ?? 0)}</MonoAmount>
      </p>
      <Separator />
      <div className="space-y-1">
        {/* `expense_date` is the recorded business date and reads on its own
            encoded day; the `date` fallback only covers a legacy row with no
            `expense_date` and is the Glide import stamp — a genuine instant
            (see `expense-columns.tsx`'s `expenseDate` doc comment) — so it
            reads in the operator's own timezone instead. */}
        <DetailRow
          label="Date"
          value={
            expense.expense_date ? formatEncodedDay(expense.expense_date) : formatDate(expense.date)
          }
        />
        <DetailRow label="Category" value={categoryName || ABSENT_VALUE} />
        <DetailRow label="Receipt" value={expense.receipt_url ? "Attached" : ABSENT_VALUE} />
      </div>
    </>
  );
}

/** Expense quick-view content, registered on import via `src/app/peek-registrations.ts`. */
export const ExpensePeekContent = createPeekContent({
  emptyMessage: "No expense found",
  render: (expense) => <ExpenseContent expense={expense} />,
  useDetail: useExpenseDetail,
});

registerPeekType("expense", ExpensePeekContent);
